import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import useTrans from "../hooks/use-trans";
import { ResponseVehicle } from "../types/api";
import { AriaLiveContext } from "./AriaLive";
import { LayoutContext } from "./Layout";
import { LocationContext } from "../app";
import useVehiclesQuery from "../api/useVehiclesQuery";
import { useFeatureAppContent } from "../use-feature-app-content";

interface SelectedAcodesContextType {
  primaryAcode: string;
  acodes: string[];
  vehicles: ResponseVehicle[];
  isFetching: boolean;
  getVehicle?: (index: number) => ResponseVehicle | null | undefined;
  setAcode: (acode: string) => void;
  getCompetitor?: (position: number) => void;
  data?: ResponseVehicle[]
}

export const SelectedAcodesContext = createContext<SelectedAcodesContextType>({
  primaryAcode: "",
  acodes: [],
  vehicles: [],
  setAcode: (acode: string) => console.log(acode),
  isFetching: false,
});

export const SelectedAcodesContextProvider = ({ children }: PropsWithChildren) => {

  const faContent = useFeatureAppContent();
  const province = useContext(LocationContext);
  const { columnCount } = useContext(LayoutContext);
  const { setRemovedMessage, setAddedMessage } = useContext(AriaLiveContext);

  const { sections: { selection } } = useTrans();

  const [ initLoad, setInitLoad ] = useState(false);
  const [ allAcodes, setAllAcodes ] = useState<string[]>([]);
  const [ activeAcodes, setActiveAcodes ] = useState<string[]>([]);
  const [ previousAcodes, setPreviousAcodes ] = useState<string[]>([]);
  const [ vehicles, setVehicles ] = useState<ResponseVehicle[]>([]);

  const enabled = !!allAcodes.length && !!province && faContent.context !== "comparetool";
  const { isFetching, data } = useVehiclesQuery(allAcodes, enabled);

  const setAcode = (acode: string) => {
    if (activeAcodes.includes(acode)) {
      const index = activeAcodes.findIndex(x => x === acode);
      const acodes = [...activeAcodes.slice(0, index), ...activeAcodes.slice(index + 1)];
      const prevVehicle = vehicles.filter(x => x.acode === acode)[0];
      const message = getMessage(prevVehicle, selection.aria_live.removed);
      if (setRemovedMessage) { setRemovedMessage(message); }
      setActiveAcodes(acodes);
    } else if (activeAcodes.length === columnCount) {
      const acodes = [...activeAcodes.slice(1), acode];
      setActiveAcodes(acodes);
    } else {
      const acodes = [...activeAcodes, acode];
      setActiveAcodes(acodes);
    }
  };

  useEffect(() => {
    const isOnCompareTool = faContent?.context === "comparetool";

    if (isOnCompareTool && !!faContent?.vehicleData) {
      setVehicles(faContent.vehicleData);
    } else if (!faContent?.vehicleData && !!faContent.vehicles) {
      const storedAcodes = faContent.vehicles.map(x => x?.acode);
      const preselectedAcodes = faContent.vehicles.filter(x => x?.preselected === "true").map(y => y.acode);
      setAllAcodes(storedAcodes);
      setActiveAcodes(preselectedAcodes);
    }
  }, [faContent]);

  const getVehicle = (index: number) => {
    return vehicles[index];
  };

  const getMessage = (vehicle: ResponseVehicle, copy: string) => {
    if (!vehicle) {
      return undefined;
    }

    return copy
      .replace(
        "%%year%%",
        vehicle.year.toString()
      )
      .replace(
        "%%brand%%",
        vehicle.make
      )
      .replace(
        "%%model%%",
        vehicle.model
      )
      .replace(
        "%%trim%%",
        vehicle.trim
      );
  }

  useEffect(() => {
    if (previousAcodes !== activeAcodes) {
      setPreviousAcodes(activeAcodes);
    }
    if (activeAcodes.length > columnCount) {
      const newAcodes = activeAcodes.slice(0, columnCount);
      setActiveAcodes(newAcodes);
    }
    if (activeAcodes.length < columnCount && previousAcodes.length === columnCount) {
      setActiveAcodes(previousAcodes);
    }
  }, [columnCount]);

  useEffect(() => {
    if (!initLoad && !!activeAcodes.length) {
      setInitLoad(true);
    };
  }, [activeAcodes]);

  useEffect(() => {
    if (data) {
      const displayedVehicles = data.filter(x => activeAcodes.includes(x.acode));
      setVehicles(displayedVehicles);
    }
  }, [data, activeAcodes]);

  useEffect(() => {
    if (data?.length && previousAcodes?.length > 1 && setAddedMessage) {
      const newVehicle = data.filter(x => {
        return !previousAcodes.includes(x.acode);
      })[0];
      if (newVehicle) {
        const message = getMessage(newVehicle, selection.aria_live.added);
        setAddedMessage(message);
      }
    }
  }, [data, previousAcodes]);

  const value = useMemo(() => ({
    primaryAcode: activeAcodes[0] || "",
    acodes: activeAcodes,
    vehicles,
    isFetching,
    getVehicle,
    setAcode,
    data
  }), [
    activeAcodes,
    vehicles,
    isFetching,
    getVehicle,
    setAcode,
    data
  ]);

  return (
    <SelectedAcodesContext.Provider value={ value }>
      { children }
    </SelectedAcodesContext.Provider>
  );
};
